import { ConfigApi, IdentityApi } from '@backstage/core-plugin-api';
import fetch from 'node-fetch';
import { Entity, EntityRelation } from '@backstage/catalog-model';

/**
 * `ApiUtil` is a utility object that provides methods for manipulating API data.
 *
 * @category DataManipulation
 */
export const ApiUtil: ApiManipulation = {
  extractBackendBaseURL: (configApi: ConfigApi) => {
    const baseUrl = configApi.getConfig('backend').getString('baseUrl');
    return baseUrl;
  },
  extractProductEntity: (entity: Entity) => {
    const name = String(entity.metadata.name);
    const namespace = String(entity.metadata.namespace);

    return { name, namespace };
  },
  createApiEndpoint: (baseUrl: string, endpoint: string) => {
    return `${baseUrl}/api/${endpoint}`;
  },
  authorizeRequest: async (identityApi: IdentityApi) => {
    const identity = await identityApi.getCredentials();
    const token = identity?.token;
    const isAuthorized = !!token;

    return {
      /**
       * Executes an authorized fetch request to the given URL with optional configuration.
       * Adds the Bearer token and default headers for JSON requests.
       *
       * @param url - The URL for the API request.
       * @param options - Optional configuration for the fetch request, such as method and headers.
       * @returns A promise that resolves with the response data parsed as JSON.
       * @throws An error if the response is unsuccessful or authorization is not available.
       */
      execute: async (url: fetch.RequestInfo, options?: fetch.RequestInit) => {
        try {
          if (!isAuthorized) {
            throw new Error('No authentication token available');
          }

          const response = await fetch(url, {
            ...options,
            method: options?.method || 'GET',
            headers: {
              ...options?.headers,
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            const responseBody = await response.json();
            console.error('Error fetching data:', responseBody);
            const errMsg =
              responseBody?.error?.message ??
              `HTTP error! Status: ${response.status}`;
            throw new Error(errMsg);
          }

          return await response.json();
        } catch (error) {
          console.error('Error fetching data:', error);
          throw error;
        }
      },
    };
  },
  getUniqueRecords: (records: Entity[]) => {
    const uniqueRecords = records.reduce((acc, record) => {
      if (record) {
        const existingRecord = acc.find(
          r => r.metadata.uid === record.metadata.uid,
        );

        if (!existingRecord) {
          acc.push(record);
        }
      }

      return acc;
    }, [] as Entity[]);

    return uniqueRecords;
  },

  getUniqueTargetRefs: (relations: Entity['relations']) => {
    if (!relations) {
      return [];
    }
    const uniqueRefs = relations.reduce((acc = [], relation) => {
      if (relation) {
        const existingRef = acc.find(r => r.targetRef === relation.targetRef);

        if (!existingRef) {
          acc.push(relation);
        }
      }

      return acc;
    }, [] as EntityRelation[]);

    return uniqueRefs;
  },
};

/**
 * `ApiManipulation` defines methods for manipulating API data.
 */
export type ApiManipulation = {
  /**
   * Extracts the base URL for the backend API from the configuration.
   *
   * @param configApi - The configuration API object.
   * @returns The base URL for the backend API.
   */
  extractBackendBaseURL: (configApi: ConfigApi) => string;

  /**
   * Extracts the name and namespace of a product entity.
   *
   * @param entity - The entity object to extract data from.
   * @returns An object with the name and namespace of the product entity.
   */
  extractProductEntity: (
    entity: Entity,
  ) => Record<'name' | 'namespace', string>;

  /**
   * Creates an API endpoint by combining the base URL and endpoint.
   *
   * @param baseUrl - The base URL for the API.
   * @param endpoint - The endpoint to append to the base URL.
   * @returns The full API endpoint URL.
   */
  createApiEndpoint: (baseUrl: string, endpoint: string) => string;

  /**
   * Authorizes a fetch request with the identity API.
   *
   * @param identityApi - The identity API object.
   * @returns An object with an `execute` method for making authorized fetch requests
   *         and handling responses.
   */
  authorizeRequest: (identityApi: IdentityApi) => Promise<{
    execute: (
      url: fetch.RequestInfo,
      options?: fetch.RequestInit,
    ) => Promise<any>;
  }>;

  /**
   * Retrieves unique records from an array of entities.
   *
   * @param records - The array of entities to filter for unique records.
   * @returns An array of unique entities.
   */
  getUniqueRecords: (records: Entity[]) => Entity[];

  /**
   * Retrieves unique target references from an array of entity relations.
   *
   * @param relations - The array of entity relations to filter for unique target references.
   * @returns An array of unique entity relations.
   */
  getUniqueTargetRefs: (relations: Entity['relations']) => EntityRelation[];
};
